import axios from "axios";
import config from "@/config/constants.js";
import { firestore } from "@/config/firebase";

export default {
  namespaced: true,
  state: {
    status: "",
    // Selected ZIP codes for current market claim
    selected_zips: [],
    // Map of taken ZIP codes: { "90210": { is_own: false }, ... }
    taken_zips: {},
    // Current user's market region (from server)
    my_market: null,
    // Billing status
    billing_status: null,
    // Subscription billing snapshot from /v1/markets/billing_info — populated
    // by fetch_billing_info, consumed by /owner/account.
    //   { subscription_status, current_price, next_renewal_at, last_payment_at,
    //     payment_method_last4, invoices: [...] }
    billing_info: null,
    // Waitlist entries
    waitlist_entries: [],
    // ZIP autocomplete data (loaded from static JSON)
    zip_data: null,
    // ZIP adjacency data (loaded from static JSON)
    adjacency_data: null,
    // Timestamp of last successful fetch_markets — used to skip redundant
    // refetches when overlays remount in quick succession (e.g. super-admin
    // opens the drawing overlay every Add/Edit Market action).
    markets_last_fetched_at: 0,
    // Firestore unsubscribe fn for the live `markets` listener. Held on
    // state so subscribe_markets is idempotent (calling twice doesn't
    // open a second channel).
    markets_listener_unsubscribe: null,
    // Debounce handle so a burst of writes (one polygon = many ZIP docs)
    // collapses into a single re-fetch.
    markets_listener_debounce: null,
  },

  mutations: {
    SET_STATUS(state, status) {
      state.status = status;
    },
    SET_SELECTED_ZIPS(state, zips) {
      state.selected_zips = zips;
    },
    ADD_ZIP(state, zip) {
      if (!state.selected_zips.includes(zip)) {
        state.selected_zips.push(zip);
      }
    },
    REMOVE_ZIP(state, zip) {
      state.selected_zips = state.selected_zips.filter(z => z !== zip);
    },
    SET_TAKEN_ZIPS(state, taken) {
      const map = {};
      for (const m of taken) {
        map[m.code] = {
          is_own: m.is_own,
          owner_tenant_id: m.owner_tenant_id || null,
          owner_name: m.owner_name || null,
          pricing_mode: m.pricing_mode || 'calculated',
          lat: m.centroid_lat || null,
          lng: m.centroid_lng || null,
          area_index: typeof m.area_index === 'number' ? m.area_index : 0,
        };
      }
      state.taken_zips = map;
    },
    SET_MY_MARKET(state, market) {
      state.my_market = market;
    },
    SET_BILLING_STATUS(state, status) {
      state.billing_status = status;
    },
    SET_BILLING_INFO(state, info) {
      state.billing_info = info;
    },
    SET_WAITLIST(state, entries) {
      state.waitlist_entries = entries;
    },
    SET_ZIP_DATA(state, data) {
      state.zip_data = data;
    },
    SET_ADJACENCY_DATA(state, data) {
      state.adjacency_data = data;
    },
    SET_MARKETS_FETCHED_AT(state, ts) {
      state.markets_last_fetched_at = ts;
    },
    SET_MARKETS_LISTENER(state, { unsubscribe, debounce }) {
      state.markets_listener_unsubscribe = unsubscribe;
      state.markets_listener_debounce = debounce;
    },
    CLEAR_MARKETS_LISTENER(state) {
      state.markets_listener_unsubscribe = null;
      state.markets_listener_debounce = null;
    },
  },

  actions: {
    /**
     * Load static ZIP code autocomplete data from public/data/zip_codes.json
     */
    async load_zip_data({ commit, state }) {
      if (state.zip_data) return; // Already loaded
      try {
        // Respect Vue CLI publicPath so this works both at root-mount
        // ('/data/...') and sub-mount ('/my-market-app/data/...').
        const response = await axios.get(process.env.BASE_URL + 'data/zip_codes.json');
        // Freeze the outer object + data array so Vue skips deep reactivity.
        // We do NOT per-entry freeze (would block the main thread on 33K).
        const frozen = Object.freeze({
          ...response.data,
          data: Object.freeze(response.data.data),
        });
        commit('SET_ZIP_DATA', frozen);
      } catch (error) {
        console.error('[market_billing] Failed to load ZIP data:', error);
        commit('SET_STATUS', 'zip_data_error');
      }
    },

    /**
     * Load static ZIP adjacency data from public/data/zip_adjacency.json
     */
    async load_adjacency_data({ commit, state }) {
      if (state.adjacency_data) return; // Already loaded
      try {
        const response = await axios.get(process.env.BASE_URL + 'data/zip_adjacency.json');
        commit('SET_ADJACENCY_DATA', response.data);
      } catch (error) {
        console.error('[market_billing] Failed to load adjacency data:', error);
      }
    },

    /**
     * Fetch taken markets and current user's market from backend.
     * Callable without an auth token (e.g. from the public /my-market quote
     * page) — the backend returns taken markets to everyone and personal
     * fields (my_market, billing_status, is_own) only to authed users.
     *
     * 2s timeout + swallow 404 so local dev against an environment that
     * doesn't have the market endpoints deployed fails fast instead of
     * hanging the UI for a Cloud Run cold start + 404 round-trip.
     */
    async fetch_markets({ commit, state }, { force = false } = {}) {
      // Skip refetch if we have data younger than 60s — the overlay
      // mounts/unmounts on every Add/Edit Market in super-admin and
      // re-pulling the entire taken_markets payload each time was the
      // dominant source of "map loads slow."
      const FRESH_MS = 60 * 1000;
      if (
        !force &&
        state.markets_last_fetched_at &&
        Date.now() - state.markets_last_fetched_at < FRESH_MS
      ) {
        return;
      }
      commit('SET_STATUS', 'loading');
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: token } : {};
        const response = await axios.get(config.api_url + 'markets', {
          headers,
          timeout: 8000,
        });
        if (response.data.code === 'SUCCESS') {
          commit('SET_TAKEN_ZIPS', response.data.data.taken_markets);
          commit('SET_MY_MARKET', response.data.data.my_market);
          commit('SET_BILLING_STATUS', response.data.data.billing_status);
          commit('SET_MARKETS_FETCHED_AT', Date.now());
        }
      } catch (error) {
        const status = error.response && error.response.status;
        // 404 = endpoint isn't deployed to this environment yet (common in
        // local dev). Timeout = backend unreachable. Neither is fatal —
        // the page still works without the reserved-territory overlay.
        if (status === 404 || error.code === 'ECONNABORTED') {
          console.warn('[market_billing] fetch_markets skipped —', status || error.code);
        } else {
          console.error('[market_billing] fetch_markets error:', error);
        }
      } finally {
        commit('SET_STATUS', '');
      }
    },

    /**
     * Open a live Firestore listener on the `markets` collection. Any
     * change anywhere — super-admin grant/edit/release, public sign-up
     * claim, manual data fix — debounces into a single fetch_markets()
     * refresh, which then drives every page that subscribes to this
     * store. Single source of truth for both admin Markets and
     * kiey.com/my-market with no refresh required.
     *
     * Idempotent: a second call while already subscribed is a no-op.
     */
    subscribe_markets({ commit, state, dispatch }) {
      if (state.markets_listener_unsubscribe) return;

      let debounceTimer = null;
      const triggerRefresh = () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          dispatch('fetch_markets', { force: true });
        }, 600);
      };

      const unsubscribe = firestore.collection('markets').onSnapshot(
        (snap) => {
          // Skip the initial snapshot if we just fetched — fetch_markets
          // already populated state and re-fetching immediately is wasted
          // work. Subsequent snapshots are real changes.
          if (state.markets_last_fetched_at &&
              Date.now() - state.markets_last_fetched_at < 2000 &&
              !snap.metadata.hasPendingWrites) {
            return;
          }
          triggerRefresh();
        },
        (error) => {
          console.warn('[market_billing] live listener error:', error);
        },
      );

      commit('SET_MARKETS_LISTENER', { unsubscribe, debounce: debounceTimer });
    },

    /**
     * Tear down the live listener — call from a component beforeDestroy
     * if you want to stop the subscription. In practice the store
     * outlives most components, so this is rarely needed; the listener
     * is cheap (one Firestore channel).
     */
    unsubscribe_markets({ commit, state }) {
      if (state.markets_listener_unsubscribe) {
        try { state.markets_listener_unsubscribe(); } catch (e) { /* noop */ }
      }
      if (state.markets_listener_debounce) {
        clearTimeout(state.markets_listener_debounce);
      }
      commit('CLEAR_MARKETS_LISTENER');
    },

    /**
     * Add a ZIP code to the selection.
     */
    add_zip({ commit, state }, zip) {
      // Check if taken by someone else
      if (state.taken_zips[zip] && !state.taken_zips[zip].is_own) {
        return { error: 'taken', message: 'This ZIP code is taken by another agent.' };
      }

      commit('ADD_ZIP', zip);
      return { success: true };
    },

    /**
     * Replace the entire selection in one commit. Used by the radius picker
     * so every slider tick is O(1) commits, not O(n). Only filters out ZIPs
     * already owned by other agents; does NOT enforce the population cap —
     * the caller decides whether to commit or reject based on total size.
     * Returns { added, skipped_taken, total_population } for the UI.
     *
     * Payload is either a plain array of ZIPs, or { zips, edit_tenant_id }.
     * In the super-admin Edit Market flow, edit_tenant_id is the tenant being
     * edited — its own ZIPs read back as taken-but-not-is_own (is_own is
     * relative to the super-user's tenant), so without this context they'd be
     * wrongly filtered out of the selection.
     */
    set_selection({ commit, state, getters }, payload) {
      const isObj = payload && !Array.isArray(payload);
      const zips = isObj ? (payload.zips || []) : (payload || []);
      const editTenantId = isObj ? (payload.edit_tenant_id || null) : null;
      const added = [];
      const skipped_taken = [];
      let total_population = 0;
      const popMap = getters.zip_population_map;

      for (const zip of zips) {
        const taken = state.taken_zips[zip];
        if (taken && !taken.is_own && taken.owner_tenant_id !== editTenantId) {
          skipped_taken.push(zip);
          continue;
        }
        added.push(zip);
        total_population += popMap[zip] || 0;
      }
      commit('SET_SELECTED_ZIPS', added);
      return { added, skipped_taken, total_population };
    },

    /**
     * Remove a ZIP code from the selection.
     */
    remove_zip({ commit }, zip) {
      commit('REMOVE_ZIP', zip);
      return { removed: [zip] };
    },

    /**
     * Claim the selected market and redirect to Stripe Checkout.
     *
     * Two callers, two flows:
     *   1) Public /my-market quote → user is now authed, has a fresh
     *      polygon in state.selected_zips, needs claim + checkout in one
     *      shot. (skip_claim=false, default.)
     *   2) Post-signup billing redirect → signup_owner already persisted
     *      the polygon to markets/, so we just need a Checkout session.
     *      (skip_claim=true.)
     *
     * `billing_terms_accepted` + `billing_terms_version` are required by
     * the backend for /markets/checkout — the /sign_up/billing page
     * collects them via the mandatory acceptance checkbox before this is
     * called. Older callers that still want to invoke checkout without
     * the terms UI must explicitly flow accepted=true + a version string.
     */
    async checkout(
      { commit, state },
      {
        skip_claim = false,
        billing_terms_accepted = false,
        billing_terms_version = null,
        // Optional Stripe coupon code captured on /sign_up/billing. We
        // forward as-is — Stripe Checkout validates server-side; backend
        // surfaces invalid / expired codes as a clean 400 + INVALID_COUPON.
        coupon_code = null,
        // Optional Free Tier Partner Agreement acceptance — only set when
        // the user applied a free-tier coupon code on /sign_up/billing
        // and ticked the inline acceptance checkbox. Backend records the
        // version + timestamp + IP next to the billing-terms audit trail.
        free_terms_accepted = false,
        free_terms_version = null,
      } = {},
    ) {
      if (!skip_claim && state.selected_zips.length === 0) {
        return { error: 'no_zips', message: 'Select at least one ZIP code.' };
      }

      commit('SET_STATUS', 'loading');
      try {
        const token = localStorage.getItem('auth_token');

        if (!skip_claim) {
          // Build zip_details map with centroids for server storage
          const zip_details = {};
          if (state.zip_data && state.zip_data.data) {
            const zipMap = {};
            for (const entry of state.zip_data.data) {
              zipMap[entry.z] = entry;
            }
            for (const zip of state.selected_zips) {
              const entry = zipMap[zip];
              if (entry) {
                zip_details[zip] = { lat: entry.la, lng: entry.ln, city: entry.c, state: entry.s };
              }
            }
          }

          // Step 1: Claim the ZIPs
          await axios.post(config.api_url + 'markets/claim', {
            zip_codes: state.selected_zips,
            zip_details,
          }, {
            headers: { Authorization: token }
          });
        }

        // Step 2: Create Stripe Checkout session. zip_codes is omitted in
        // the post-signup path — backend reads the persisted market for the
        // tenant and prices it server-side.
        const checkout_payload = skip_claim ? {} : { zip_codes: state.selected_zips };
        if (billing_terms_accepted) {
          checkout_payload.billing_terms_accepted = true;
          checkout_payload.billing_terms_version = billing_terms_version || '';
        }
        if (coupon_code && typeof coupon_code === 'string') {
          checkout_payload.coupon_code = coupon_code.trim();
        }
        if (free_terms_accepted && typeof free_terms_version === 'string' && free_terms_version) {
          checkout_payload.free_terms_accepted = true;
          checkout_payload.free_terms_version = free_terms_version;
        }

        let response;
        try {
          response = await axios.post(config.api_url + 'markets/checkout', checkout_payload, {
            headers: { Authorization: token }
          });
        } catch (checkoutError) {
          // Checkout failed — rollback the claim by releasing ZIPs (only
          // when we just claimed in this same call).
          console.error('[market_billing] Checkout failed, rolling back claim:', checkoutError);
          if (!skip_claim) {
            try {
              await axios.post(config.api_url + 'markets/release', {
                zip_codes: state.selected_zips,
              }, {
                headers: { Authorization: token }
              });
            } catch (releaseError) {
              console.error('[market_billing] Rollback failed:', releaseError);
            }
          }
          commit('SET_STATUS', 'error');
          const message = checkoutError.response?.data?.message || 'Failed to create checkout session.';
          return { error: 'checkout_failed', message };
        }

        if (response.data.code === 'SUCCESS') {
          commit('SET_STATUS', '');
          return { success: true, url: response.data.data.url };
        }

        commit('SET_STATUS', 'error');
        return { error: 'checkout_failed', message: 'Failed to create checkout session.' };
      } catch (error) {
        console.error('[market_billing] checkout error:', error);
        commit('SET_STATUS', 'error');
        const message = error.response?.data?.message || 'Failed to process checkout.';
        return { error: 'checkout_failed', message };
      }
    },

    /**
     * Join waitlist for a taken ZIP code.
     */
    async join_waitlist(_, zip_code) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.post(config.api_url + 'markets/waitlist', {
          zip_code,
        }, {
          headers: { Authorization: token }
        });

        if (response.data.code === 'SUCCESS') {
          return { success: true, data: response.data.data };
        }
        return { error: 'failed', message: 'Failed to join waitlist.' };
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to join waitlist.';
        return { error: 'failed', message };
      }
    },

    /**
     * Open the Stripe Customer Portal so the user can update payment method,
     * download invoices, etc. Backend returns a hosted URL; we redirect via
     * window.location so the portal owns the entire UI surface.
     */
    async billing_portal({ commit }) {
      commit('SET_STATUS', 'loading');
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(config.api_url + 'markets/billing_portal', {
          headers: { Authorization: token },
        });
        if (response.data.code === 'SUCCESS' && response.data.data && response.data.data.url) {
          window.location.href = response.data.data.url;
          return { success: true };
        }
        commit('SET_STATUS', 'error');
        return { error: 'portal_failed', message: 'Failed to open billing portal.' };
      } catch (error) {
        console.error('[market_billing] billing_portal error:', error);
        commit('SET_STATUS', 'error');
        const message = error.response?.data?.message || 'Failed to open billing portal.';
        return { error: 'portal_failed', message };
      } finally {
        commit('SET_STATUS', '');
      }
    },

    /**
     * Pull the subscription/invoice snapshot for the logged-in tenant. Used
     * by /owner/account to render the Billing section. Swallows errors to
     * the console — the Account Info form should still render even if
     * billing_info isn't reachable.
     */
    async fetch_billing_info({ commit }) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(config.api_url + 'markets/billing_info', {
          headers: { Authorization: token },
        });
        if (response.data.code === 'SUCCESS') {
          commit('SET_BILLING_INFO', response.data.data);
          return { success: true, data: response.data.data };
        }
        return { error: 'failed', message: 'Failed to load billing info.' };
      } catch (error) {
        console.error('[market_billing] fetch_billing_info error:', error);
        const message = error.response?.data?.message || 'Failed to load billing info.';
        return { error: 'failed', message };
      }
    },

    /**
     * Schedule cancellation of the active subscription. The backend keeps the
     * market exclusivity intact for 30 days and returns the prorated refund
     * amount + cancel-effective timestamp. Frontend surfaces those values
     * back to the user; account record is NOT deleted here (release_market
     * handles its own cleanup).
     */
    async release(_, payload = {}) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.post(config.api_url + 'markets/release', payload, {
          headers: { Authorization: token },
        });
        if (response.data.code === 'SUCCESS') {
          return { success: true, data: response.data.data };
        }
        return { error: 'failed', message: response.data.message || 'Failed to schedule cancellation.' };
      } catch (error) {
        console.error('[market_billing] release error:', error);
        const message = error.response?.data?.message || 'Failed to schedule cancellation.';
        return { error: 'failed', message };
      }
    },

    /**
     * Undo a scheduled cancellation. Clears cancel_at on the Stripe
     * subscription and the corresponding tenant doc fields so the user
     * stays on the subscription past the previously-scheduled end date.
     */
    async resume() {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.post(config.api_url + 'markets/resume', {}, {
          headers: { Authorization: token },
        });
        if (response.data.code === 'SUCCESS') {
          return { success: true, data: response.data.data };
        }
        return { error: 'failed', message: response.data.message || 'Failed to resume subscription.' };
      } catch (error) {
        console.error('[market_billing] resume error:', error);
        const message = error.response?.data?.message || 'Failed to resume subscription.';
        return { error: 'failed', message };
      }
    },

    /**
     * Fetch current user's waitlist entries.
     */
    async fetch_waitlist({ commit }) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(config.api_url + 'markets/waitlist', {
          headers: { Authorization: token }
        });

        if (response.data.code === 'SUCCESS') {
          commit('SET_WAITLIST', response.data.data.entries);
        }
      } catch (error) {
        console.error('[market_billing] fetch_waitlist error:', error);
      }
    },
  },

  getters: {
    /**
     * Check if a ZIP code is adjacent to any ZIP in the current selection.
     */
    is_adjacent_to_selection: (state) => (zip) => {
      if (!state.adjacency_data || !state.adjacency_data.adj) return true; // Allow if no adjacency data loaded
      for (const selected of state.selected_zips) {
        const neighbors = state.adjacency_data.adj[selected] || [];
        if (neighbors.includes(zip)) return true;
      }
      return false;
    },

    /**
     * Find ZIPs that would become orphaned (disconnected from the main group)
     * if certain ZIPs were the only ones remaining.
     */
    find_orphaned_zips: (state) => (remaining) => {
      if (!state.adjacency_data || !state.adjacency_data.adj || remaining.length <= 1) return [];

      // BFS from the first ZIP to find all reachable ZIPs
      const adj = state.adjacency_data.adj;
      const remainingSet = new Set(remaining);
      const visited = new Set();
      const queue = [remaining[0]];
      visited.add(remaining[0]);

      while (queue.length > 0) {
        const current = queue.shift();
        const neighbors = adj[current] || [];
        for (const neighbor of neighbors) {
          if (remainingSet.has(neighbor) && !visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        }
      }

      // Any remaining ZIP not visited is orphaned
      return remaining.filter(z => !visited.has(z));
    },

    /**
     * Search ZIP autocomplete data. Returns top 10 matches.
     */
    search_zips: (state) => (query) => {
      if (!state.zip_data || !state.zip_data.data || !query) return [];
      const q = query.trim().toLowerCase();
      if (q.length < 2) return [];

      const results = [];
      for (const entry of state.zip_data.data) {
        // Match by ZIP code prefix or city name
        if (entry.z.startsWith(q) || entry.c.toLowerCase().includes(q)) {
          const isTaken = !!state.taken_zips[entry.z];
          const isOwn = isTaken && state.taken_zips[entry.z].is_own;
          const isSelected = state.selected_zips.includes(entry.z);

          results.push({
            zip: entry.z,
            city: entry.c,
            state: entry.s,
            lat: entry.la,
            lng: entry.ln,
            label: `${entry.z} — ${entry.c}, ${entry.s}`,
            is_taken: isTaken,
            is_own: isOwn,
            is_selected: isSelected,
          });

          if (results.length >= 50) break;
        }
      }
      return results;
    },

    is_loading: (state) => state.status === 'loading',
    has_selection: (state) => state.selected_zips.length > 0,
    selected_count: (state) => state.selected_zips.length,

    /**
     * Lookup table from ZIP -> population (from zip_codes.json `p` field).
     * Cached implicitly by Vuex getter memoization on state.zip_data.
     */
    zip_population_map: (state) => {
      if (!state.zip_data || !state.zip_data.data) return {};
      const map = {};
      for (const entry of state.zip_data.data) {
        map[entry.z] = entry.p || 0;
      }
      return map;
    },

    /**
     * Population for a given ZIP (0 if unknown).
     */
    population_for_zip: (state, getters) => (zip) => {
      return getters.zip_population_map[zip] || 0;
    },

    /**
     * Summed population of the current selection.
     */
    selected_population: (state, getters) => {
      const map = getters.zip_population_map;
      let total = 0;
      for (const zip of state.selected_zips) {
        total += map[zip] || 0;
      }
      return total;
    },

    /**
     * Taken markets grouped by (owner_tenant_id, area_index) with one convex
     * hull per group. Multi-area tenants emit one entry per area; single-area
     * tenants (the default — area_index 0 for all their ZIPs) emit a single
     * entry, identical to legacy behavior.
     *
     * Returns: { [`${ownerId}::${areaIndex}`]: { owner_tenant_id, area_index,
     *           owner_name, pricing_mode, points, zips, hull, swap_zoom,
     *           label_zoom } }
     */
    taken_markets_by_owner: (state) => {
      const byKey = {};
      for (const [zip, data] of Object.entries(state.taken_zips)) {
        if (data.is_own) continue;
        if (typeof data.lat !== 'number' || typeof data.lng !== 'number') continue;
        const ownerId = data.owner_tenant_id || data.owner_name || 'unknown';
        const areaIndex = typeof data.area_index === 'number' ? data.area_index : 0;
        const key = `${ownerId}::${areaIndex}`;
        if (!byKey[key]) {
          byKey[key] = {
            owner_tenant_id: data.owner_tenant_id || null,
            area_index: areaIndex,
            owner_name: data.owner_name || null,
            pricing_mode: data.pricing_mode || 'calculated',
            points: [],
            zips: [],
          };
        }
        // Pricing mode is mirrored to every ZIP doc but be defensive in case
        // of partial/older data — first non-null wins.
        if (!byKey[key].pricing_mode && data.pricing_mode) {
          byKey[key].pricing_mode = data.pricing_mode;
        }
        byKey[key].points.push({ lat: data.lat, lng: data.lng });
        byKey[key].zips.push(zip);
      }
      for (const key of Object.keys(byKey)) {
        byKey[key].hull = convexHull(byKey[key].points);
        byKey[key].swap_zoom = swapZoomForPoints(byKey[key].points);
        byKey[key].label_zoom = labelZoomForPointsAndText(
          byKey[key].points,
          labelTextFor(byKey[key]),
        );
      }
      return byKey;
    },

    /**
     * Same as taken_markets_by_owner but does NOT skip the caller's own
     * markets. Used by the super-admin Markets page, where the operator
     * needs to see every granted market on the map regardless of which
     * tenant they personally happen to belong to. The public /my-market
     * map keeps using the filtered version so the user isn't blocked
     * from claiming their own ZIPs.
     */
    all_taken_markets_by_owner: (state) => {
      const byKey = {};
      for (const [zip, data] of Object.entries(state.taken_zips)) {
        if (typeof data.lat !== 'number' || typeof data.lng !== 'number') continue;
        const ownerId = data.owner_tenant_id || data.owner_name || 'unknown';
        const areaIndex = typeof data.area_index === 'number' ? data.area_index : 0;
        const key = `${ownerId}::${areaIndex}`;
        if (!byKey[key]) {
          byKey[key] = {
            owner_tenant_id: data.owner_tenant_id || null,
            area_index: areaIndex,
            owner_name: data.owner_name || null,
            pricing_mode: data.pricing_mode || 'calculated',
            points: [],
            zips: [],
          };
        }
        if (!byKey[key].pricing_mode && data.pricing_mode) {
          byKey[key].pricing_mode = data.pricing_mode;
        }
        byKey[key].points.push({ lat: data.lat, lng: data.lng });
        byKey[key].zips.push(zip);
      }
      for (const key of Object.keys(byKey)) {
        byKey[key].hull = convexHull(byKey[key].points);
        byKey[key].swap_zoom = swapZoomForPoints(byKey[key].points);
        byKey[key].label_zoom = labelZoomForPointsAndText(
          byKey[key].points,
          labelTextFor(byKey[key]),
        );
      }
      return byKey;
    },
  },
};

/**
 * Compute the polygon's screen-space dimensions in degrees, accounting
 * for Mercator longitude compression at the polygon's mean latitude.
 * At 40°N a 1° lng span is only 0.766° wide on screen — without this
 * adjustment, polygon-fits-viewport math is off by ~25% in the US.
 *
 * Returns:
 *   { lat_span, lng_span_screen, max_screen_span, mean_lat }
 * Empty/single-point input returns all zeros.
 */
function screenSpanForPoints(points) {
  if (!points || points.length === 0) {
    return { lat_span: 0, lng_span_screen: 0, max_screen_span: 0, mean_lat: 0 };
  }
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  const lat_span = maxLat - minLat;
  const lng_span = maxLng - minLng;
  const mean_lat = (minLat + maxLat) / 2;
  const lng_span_screen = lng_span * Math.cos(mean_lat * Math.PI / 180);
  return {
    lat_span,
    lng_span_screen,
    max_screen_span: Math.max(lat_span, lng_span_screen),
    mean_lat,
  };
}

/**
 * Pick the Google Maps zoom level at which a polygon should swap from
 * dot to polygon outline. The polygon should only appear when the user
 * has zoomed in far enough that the territory borders are clearly
 * recognizable — i.e. state-level zoom, not country-level.
 *
 * Polygon pixel size at zoom z = span_deg * 256 * 2^z / 360. With
 * multiplier 300, a market spanning ~1.5° (typical metro) flips at z=7
 * (clear state-detail view); a small ~0.3° market flips at z=9; very
 * large multi-state markets flip earlier but still well past the
 * country view. Below state level the user only sees a dot.
 */
function swapZoomForPoints(points) {
  const { max_screen_span } = screenSpanForPoints(points);
  if (max_screen_span <= 0) return 12;
  const z = Math.floor(Math.log2(300 / max_screen_span));
  return Math.max(3, Math.min(12, z));
}

/**
 * The first zoom at which the polygon's on-screen width is at least as
 * wide as the rendered label pill. Used to gate the label so it never
 * appears wider than the polygon it's annotating.
 *
 * The pill width is computed from the actual rendered text — same
 * formula MarketPolygonLabel uses (width = max(96, len*7.2 + 44)) plus
 * a small safety margin so the pill sits comfortably *inside* the
 * polygon, not crammed edge-to-edge. Returns Infinity for degenerate
 * single-point markets so the label never tries to render.
 */
function labelZoomForPointsAndText(points, label_text) {
  const { lng_span_screen } = screenSpanForPoints(points);
  if (lng_span_screen <= 0) return Infinity;
  const text_len = (label_text || 'RESERVED').length;
  const pill_width = Math.max(96, Math.round(text_len * 7.2 + 44));
  // Ask for a polygon ~25% wider than the pill so it doesn't kiss the
  // polygon edges. polygon_px = lng_span_screen * 256 * 2^z / 360, so
  //   2^z = 1.25 * pill_width * 360 / (256 * lng_span_screen)
  //       = 1.7578 * pill_width / lng_span_screen
  const target_2z = 1.7578 * pill_width / lng_span_screen;
  const z = Math.ceil(Math.log2(target_2z));
  return Math.max(3, Math.min(20, z));
}

/**
 * Compute the actual text MarketPolygonLabel will render for a market.
 * Free + named owner → owner name (uppercased). Otherwise → "RESERVED".
 * Mirrors the logic in MarketPolygonLabel.vue#text.
 */
function labelTextFor(market) {
  if (!market) return 'RESERVED';
  if (market.pricing_mode === 'free' && market.owner_name && market.owner_name.trim()) {
    return market.owner_name.trim().toUpperCase();
  }
  return 'RESERVED';
}

// Andrew's monotone-chain convex hull — O(n log n). Returns the outer ring
// as [{lat,lng}, ...] in CCW order, or the original points if fewer than 3.
function convexHull(points) {
  if (!points || points.length < 3) return points ? [...points] : [];
  const sorted = [...points].sort((a, b) => a.lng - b.lng || a.lat - b.lat);
  const cross = (O, A, B) =>
    (A.lng - O.lng) * (B.lat - O.lat) - (A.lat - O.lat) * (B.lng - O.lng);
  const lower = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}
